import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  BENEFIT_CAMPAIGN_ADMIN_PATH,
  benefitCampaignFormFromResponse,
  defaultBenefitCampaignForm,
  serializeBenefitCampaignForm,
  type BenefitCampaignForm,
  type BenefitCampaignResponse,
} from './benefitCampaign';

type BenefitCampaignSettingsProps = {
  getAuthHeaders: () => Record<string, string>;
};

const inputClasses =
  'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

export default function BenefitCampaignSettings({ getAuthHeaders }: BenefitCampaignSettingsProps) {
  const [form, setForm] = useState<BenefitCampaignForm>(() => defaultBenefitCampaignForm());
  const [status, setStatus] = useState<string>('설정 없음');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<BenefitCampaignResponse>(BENEFIT_CAMPAIGN_ADMIN_PATH, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const campaign = response.data?.campaign;
      if (!campaign) {
        setStatus('설정 없음');
        return;
      }

      setForm(benefitCampaignFormFromResponse(campaign));
      setStatus(campaign.status || '설정됨');
    } catch (fetchError) {
      console.error('Failed to fetch benefit campaign:', fetchError);
      setStatus('조회 실패');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  const handleChange = <K extends keyof BenefitCampaignForm>(key: K, value: BenefitCampaignForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const payload = serializeBenefitCampaignForm(form);
      const response = await axios.put(BENEFIT_CAMPAIGN_ADMIN_PATH, payload, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setStatus(response.data?.campaign?.status || (form.isActive ? 'active' : 'inactive'));
      setMessage('혜택 캠페인 설정을 저장했습니다.');
      await loadCampaign();
    } catch (saveError) {
      console.error('Failed to save benefit campaign:', saveError);
      setMessage(saveError instanceof Error ? saveError.message : '혜택 캠페인 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>혜택 캠페인 설정</CardTitle>
        <p className="text-sm text-muted-foreground">
          이 기간 안에 로그인한 free 사용자에게만 Pro 무료 혜택을 지급합니다. 현재 상태: {loading ? '조회 중' : status}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">캠페인 이름</label>
            <input className={inputClasses} value={form.name} onChange={(event) => handleChange('name', event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">시작일시</label>
            <input
              className={inputClasses}
              type="datetime-local"
              value={form.startsAt}
              onChange={(event) => handleChange('startsAt', event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">종료일시</label>
            <input
              className={inputClasses}
              type="datetime-local"
              value={form.endsAt}
              onChange={(event) => handleChange('endsAt', event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">지급 일수</label>
            <input
              className={inputClasses}
              min={1}
              type="number"
              value={form.durationDays}
              onChange={(event) => handleChange('durationDays', Math.max(1, Number(event.target.value) || 1))}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">공지 제목</label>
            <input className={inputClasses} value={form.title} onChange={(event) => handleChange('title', event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">공지 본문</label>
            <input className={inputClasses} value={form.body} onChange={(event) => handleChange('body', event.target.value)} />
          </div>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => handleChange('isActive', event.target.checked)}
            />
            활성화
          </label>
          <div className="flex items-center gap-3">
            {message ? <span className="text-sm text-muted-foreground">{message}</span> : null}
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              저장
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
